(() => {
  const freeShippingThresholdCents = 5500;
  const mondialRelayCents = 199;
  const countryStorageKey = 'pomlie_shipping_country_v1';
  const shippingRatesByWeight = [
    { maxGrams: 360, cents: 699 },
    { maxGrams: 610, cents: 810 },
    { maxGrams: 860, cents: 880 },
    { maxGrams: 1860, cents: 1015 },
    { maxGrams: Infinity, cents: 1560 }
  ];

  let refreshTimer = null;
  let refreshSequence = 0;

  const localeRoot = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : root + '/';
  };

  const formatMoney = (cents) => new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR'
  }).format((Number(cents) || 0) / 100);

  const isGiftCard = (item) => Boolean(item?.gift_card)
    || /e[-\s]?carte cadeau/i.test(item?.title || item?.product_title || '');

  const getStoredCountry = () => {
    try {
      const stored = window.localStorage.getItem(countryStorageKey);
      if (stored === 'FR' || stored === 'OTHER') return stored;
    } catch (error) {
      // Le stockage local peut être indisponible.
    }
    return null;
  };

  const getCountry = () => getStoredCountry() || document.querySelector('[data-shipping-country]')?.value || 'FR';

  const setText = (element, value) => {
    if (element && element.textContent !== value) element.textContent = value;
  };

  const setHidden = (element, hidden) => {
    if (element && element.hidden !== hidden) element.hidden = hidden;
  };

  const getCart = async () => {
    const response = await fetch(localeRoot() + 'cart.js', {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Impossible de lire le panier.');
    return response.json();
  };

  const getCartTotals = (cart) => {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const physicalItems = items.filter((item) => !isGiftCard(item));
    const physicalSubtotalCents = physicalItems.reduce(
      (total, item) => total + (Number(item.final_line_price) || Number(item.line_price) || 0),
      0
    );
    const totalCents = Number(cart?.total_price) || physicalSubtotalCents;
    const totalGrams = physicalItems.reduce(
      (total, item) => total + ((Number(item.grams) || 0) * (Number(item.quantity) || 0)),
      0
    );
    return {
      items,
      physicalItems,
      physicalSubtotalCents,
      totalCents,
      totalGrams
    };
  };

  const calculateLaPosteCents = (totalGrams) => {
    const rate = shippingRatesByWeight.find((item) => totalGrams <= item.maxGrams);
    return rate ? rate.cents : shippingRatesByWeight[shippingRatesByWeight.length - 1].cents;
  };

  const renderCart = (cart) => {
    const containers = document.querySelectorAll('[data-shipping-estimate]');
    if (!containers.length) return;

    const totals = getCartTotals(cart);
    const country = getCountry();
    const hasPhysicalProducts = totals.physicalItems.length > 0;

    containers.forEach((container) => {
      const countrySelect = container.querySelector('[data-shipping-country]');
      const progress = container.querySelector('[data-shipping-progress]');
      const progressFill = container.querySelector('[data-shipping-progress-fill]');
      const progressMessage = container.querySelector('[data-shipping-progress-message]');
      const rateDetails = container.querySelector('[data-shipping-rate-details]');
      const destinationMessage = container.querySelector('[data-shipping-destination-message]');
      const disclaimer = container.querySelector('[data-shipping-disclaimer]');
      const progressValue = container.querySelector('[data-shipping-progress-value]');

      setHidden(container, !totals.items.length || !hasPhysicalProducts);
      if (countrySelect && countrySelect.value !== country) countrySelect.value = country;

      if (!totals.items.length || !hasPhysicalProducts) return;

      if (country !== 'FR') {
        setHidden(progress, true);
        setHidden(rateDetails, true);
        setHidden(progressMessage, true);
        setHidden(disclaimer, true);
        setHidden(destinationMessage, false);
        setText(destinationMessage, 'Les frais de livraison seront calculés à l’étape suivante.');
        return;
      }

      const isFreeMondialRelay = totals.physicalSubtotalCents >= freeShippingThresholdCents;
      const mondialRelayShippingCents = isFreeMondialRelay ? 0 : mondialRelayCents;
      const laPosteShippingCents = isFreeMondialRelay ? 0 : calculateLaPosteCents(totals.totalGrams);
      const remainingCents = Math.max(0, freeShippingThresholdCents - totals.physicalSubtotalCents);
      const progressPercent = Math.min(100, (totals.physicalSubtotalCents / freeShippingThresholdCents) * 100);

      if (progress) {
        progress.setAttribute('aria-valuenow', String(Math.min(freeShippingThresholdCents, totals.physicalSubtotalCents) / 100));
        progress.setAttribute('aria-valuetext', formatMoney(totals.physicalSubtotalCents) + ' sur ' + formatMoney(freeShippingThresholdCents));
      }

      setHidden(progress, false);
      setHidden(rateDetails, false);
      setHidden(progressMessage, false);
      setHidden(disclaimer, false);
      setHidden(destinationMessage, true);

      if (progressFill) {
        const nextWidth = progressPercent.toFixed(2) + '%';
        if (progressFill.style.width !== nextWidth) progressFill.style.width = nextWidth;
      }
      setText(progressValue, formatMoney(totals.physicalSubtotalCents) + ' / ' + formatMoney(freeShippingThresholdCents));

      if (isFreeMondialRelay) {
        setText(progressMessage, 'Livraison offerte en France métropolitaine ✨');
      } else {
        setText(progressMessage, 'Encore ' + formatMoney(remainingCents) + ' pour la livraison offerte');
      }

      const ratesMarkup = isFreeMondialRelay
        ? [
          '<div class="shipping-estimate__rate-row"><span>Livraison</span><strong>Offerte<small>Total estimé : ',
          formatMoney(totals.totalCents),
          '</small></strong></div>'
        ].join('')
        : [
          '<div class="shipping-estimate__rate-row"><span>Mondial Relay</span><strong>',
          formatMoney(mondialRelayShippingCents),
          '<small>Total estimé : ',
          formatMoney(totals.totalCents + mondialRelayShippingCents),
          '</small></strong></div>',
          '<div class="shipping-estimate__rate-row"><span>La Poste <small>selon le poids</small></span><strong>',
          formatMoney(laPosteShippingCents),
          '<small>Total estimé : ',
          formatMoney(totals.totalCents + laPosteShippingCents),
          '</small></strong></div>'
        ].join('');

      if (rateDetails && rateDetails.innerHTML !== ratesMarkup) rateDetails.innerHTML = ratesMarkup;
      setText(disclaimer, 'Estimation pour la France métropolitaine. Le montant final est confirmé lors du choix de la livraison.');
    });
  };

  const refresh = async (cartData = null) => {
    const sequence = ++refreshSequence;
    try {
      const cart = cartData || await getCart();
      if (sequence === refreshSequence) renderCart(cart);
    } catch (error) {
      // Le panier reste utilisable si l’estimation est momentanément indisponible.
    }
  };

  const scheduleRefresh = (delay = 80, cartData = null) => {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => refresh(cartData), delay);
  };

  const rememberCountry = (value) => {
    try {
      window.localStorage.setItem(countryStorageKey, value);
    } catch (error) {
      // Le stockage local peut être indisponible.
    }
  };

  const observeCartDrawer = () => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || drawer.dataset.shippingEstimateObserved === 'true') return;
    drawer.dataset.shippingEstimateObserved = 'true';
    new MutationObserver(() => scheduleRefresh(120)).observe(drawer, { childList: true, subtree: true });
  };

  const init = () => {
    if (!document.querySelector('[data-shipping-estimate]')) return;
    observeCartDrawer();
    scheduleRefresh(0);

    document.addEventListener('change', (event) => {
      const countrySelect = event.target.closest('[data-shipping-country]');
      if (!countrySelect) return;
      rememberCountry(countrySelect.value);
      scheduleRefresh(0);
    });

    document.addEventListener('cart:updated', () => scheduleRefresh(80));
    document.addEventListener('cart:toggle', () => scheduleRefresh(120));

    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event?.cartData) renderCart(event.cartData);
          scheduleRefresh(160, event?.cartData || null);
        });
      }
    } catch (error) {
      // Le thème peut ne pas exposer le bus d’événements sur certaines pages.
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();